import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Check } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import LinkButton from '@/components/ui/link-button'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import Difficulty, {
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import Season from 'app/frontend/types/custom/Season'
import EventActivityBlock from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/EventActivityBlock'
import EventActivitySelectionCount from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/components/EventActivitySelectionCount'
import DefaultSetupProviders from './DefaultSetupProviders'
import DefaultSetupStatusSelect from './DefaultSetupStatusSelect'
import {
  buildDefaultSetupActivity,
  buildDefaultSetupEntries,
} from './defaultSetupEntries'
import useDefaultSetupMutations from './useDefaultSetupMutations'

type Props = {
  encounter: wowaudit.Encounter
  instance?: wowaudit.Instance
  difficulty: Difficulty
  season?: Season
  canEdit: boolean
}

const DefaultSetupCard = ({
  encounter,
  instance,
  difficulty,
  season,
  canEdit,
}: Props) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const {
    data: { gameVersion },
  } = usePageInfo()
  const {
    isSetupsPending,
    isSaving,
    setupFor,
    upsertEnabled,
    isUpsertPending,
    updateSelection: mutateSelection,
  } = useDefaultSetupMutations(team.id, difficulty)
  const setup = setupFor(encounter.id)
  const enabled = setup?.enabled ?? false
  const canInteract = canEdit
  const showMemberOverlay = !canEdit && !enabled
  const upsertPending = isUpsertPending(encounter.id)

  const activity = useMemo(
    () =>
      buildDefaultSetupActivity({
        encounter,
        instance,
        difficulty,
        gameVersion,
      }),
    [difficulty, encounter, gameVersion, instance],
  )
  const entries = useMemo(
    () =>
      buildDefaultSetupEntries({
        team,
        members: team.members ?? [],
        activityId: activity.id,
        selections: setup?.selections ?? {},
      }),
    [activity.id, setup?.selections, team],
  )

  const updateSelection = useCallback(
    (
      characterId: number,
      updates: {
        role?: string
        specId?: number | null
        characterId?: number
        selected?: boolean
      },
    ) => {
      if (!canInteract) return
      mutateSelection(encounter.id, characterId, updates)
    },
    [canInteract, encounter.id, mutateSelection],
  )

  const showSelectionCount = canEdit || enabled

  return (
    <DefaultSetupProviders
      activities={[activity]}
      entries={entries}
      onUpdateSelection={updateSelection}
    >
      <div className='space-y-3'>
        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-center gap-3 min-w-0'>
            <h4 className='m-0 truncate'>
              {t('dashboard.team.plans.default-setup.title')}
            </h4>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex'>
                  <Badge variant='outline'>
                    {isSaving ? (
                      <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    ) : (
                      <Check className='h-3.5 w-3.5 text-success-foreground' />
                    )}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {isSaving ? 'Saving changes...' : 'Up to date'}
              </TooltipContent>
            </Tooltip>
            {showSelectionCount && !isSetupsPending ? (
              <EventActivitySelectionCount activity={activity} />
            ) : null}
          </div>
          <div className='flex items-center gap-2 shrink-0'>
            {isSetupsPending ? null : (
              <DefaultSetupStatusSelect
                enabled={enabled}
                disabled={!canEdit}
                isPending={upsertPending}
                onEnabledChange={(nextEnabled) =>
                  upsertEnabled(encounter.id, nextEnabled)
                }
              />
            )}
            {canEdit && season ? (
              <LinkButton
                to={`${team.path}/events/default-setups/${season.id}/${difficulty}`}
                size='sm'
              >
                {t('dashboard.team.plans.default-setup.view-all')}
              </LinkButton>
            ) : null}
          </div>
        </div>
        <Separator />
        <p className='mb-6'>
          {t('dashboard.team.plans.default-setup.description', {
            difficulty: difficultiesLabels[difficulty],
            encounter: encounter.name,
          })}
        </p>
        <div className='max-w-5xl mx-auto space-y-3'>
          {isSetupsPending ? (
            <div className='flex justify-center py-10'>
              <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            </div>
          ) : (
            <>
              <Alert
                variant={enabled ? 'success' : 'warning'}
                className='flex items-center justify-between gap-3 py-2 px-3'
              >
                <AlertDescription className='min-w-0 flex-1'>
                  <strong>
                    {enabled
                      ? t('dashboard.team.plans.default-setup.active')
                      : t('dashboard.team.plans.default-setup.inactive-label')}
                    .{' '}
                  </strong>
                  {enabled
                    ? t('dashboard.team.plans.default-setup.active-description')
                    : t(
                        'dashboard.team.plans.default-setup.inactive-description',
                      )}
                </AlertDescription>
                {canEdit ? (
                  <Button
                    type='button'
                    variant='outline'
                    size='xs'
                    className='shrink-0'
                    disabled={upsertPending}
                    onClick={() => upsertEnabled(encounter.id, !enabled)}
                  >
                    {enabled
                      ? t('dashboard.team.plans.default-setup.set-inactive')
                      : t('dashboard.team.plans.default-setup.set-active')}
                  </Button>
                ) : null}
              </Alert>
              <div className='relative'>
                <div
                  className={cn(
                    !canInteract && 'pointer-events-none',
                    showMemberOverlay && 'select-none opacity-30',
                  )}
                >
                  <EventActivityBlock
                    activity={activity}
                    hideStatus
                    hideEventActions
                  />
                </div>
                {showMemberOverlay ? (
                  <div className='absolute inset-0 flex items-center justify-center'>
                    <div className='rounded-md border border-border bg-background px-4 py-2 font-medium backdrop-blur-sm text-center'>
                      <div>
                        {t('dashboard.team.plans.default-setup.inactive')}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </DefaultSetupProviders>
  )
}

export default DefaultSetupCard
